import React, { useMemo } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { Close as RemoveIcon } from '@mui/icons-material';
import { PhxBadge } from '_lex/_atoms/PhxBadge';
import { translate as t } from 'hooks/useTranslations';
import { FieldBankParams } from './index';
import useFieldCategories from '../_data_sources/useFieldCategories';

interface FiltersGroupProps {
  params: FieldBankParams;
  updateParams: (updatedParams: FieldBankParams) => void;
  clearParams: () => void;
  consolidatedView?: boolean; // Show single "Filters: {count}" chip instead of individual chips
}

const FiltersGroup: React.FC<FiltersGroupProps> = ({ params, updateParams, clearParams, consolidatedView = false }) => {
  const { data: fieldCategories = [] } = useFieldCategories();

  const activeFilters = useMemo(() => {
    const filters: Array<{
      key: string;
      label: string;
      value: string | number;
      removeAction: () => void;
    }> = [];

    // Search filter
    if (params.search) {
      filters.push({
        key: 'search',
        label: t('performanceReporting.fieldBank.list.filters.search') || 'Search',
        value: params.search,
        removeAction: () => updateParams({ search: null }),
      });
    }

    // Status filter
    if (params.status) {
      filters.push({
        key: 'status',
        label: t('performanceReporting.fieldBank.list.filters.status') || 'Status',
        value: t(`performanceReporting.fieldBank.status.${params.status}`) || params.status,
        removeAction: () => updateParams({ status: null }),
      });
    }

    // Field types filters (array)
    if (params.fieldTypes && params.fieldTypes.length > 0) {
      params.fieldTypes.forEach((fieldType) => {
        filters.push({
          key: `fieldType-${fieldType}`,
          label: t('performanceReporting.fieldBank.list.filters.fieldTypes') || 'Data Type',
          value: t(`performanceReporting.fieldBank.fieldType.${fieldType}`) || fieldType,
          removeAction: () => {
            const updatedFieldTypes = params.fieldTypes?.filter((type) => type !== fieldType) || [];
            updateParams({
              fieldTypes: updatedFieldTypes.length > 0 ? updatedFieldTypes : null,
            });
          },
        });
      });
    }

    // Field category filters (array)
    if (params.fieldCategoryIds && params.fieldCategoryIds.length > 0) {
      params.fieldCategoryIds.forEach((categoryId) => {
        const category = fieldCategories.find((cat) => cat.id === categoryId);
        filters.push({
          key: `category-${categoryId}`,
          label: t('performanceReporting.fieldBank.list.filters.fieldCategoryIds') || 'Category',
          value: category?.name || `Category ${categoryId}`,
          removeAction: () => {
            const updatedCategoryIds = params.fieldCategoryIds?.filter((id) => id !== categoryId) || [];
            updateParams({
              fieldCategoryIds: updatedCategoryIds.length > 0 ? updatedCategoryIds : null,
            });
          },
        });
      });
    }

    return filters;
  }, [params, fieldCategories, updateParams]);

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <Box>
      <Stack
        direction='row'
        spacing={3}
        flexWrap='wrap'
        useFlexGap
        alignItems='center'
      >
        {consolidatedView ? (
          // Consolidated view: Single chip showing "Filters" with PhxBadge count
          <Chip
            key='filters-consolidated'
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{t('performanceReporting.templates.builder.fieldBank.filters.label')}</span>
                <PhxBadge
                  color='primary'
                  size='sm'
                >
                  {activeFilters.length}
                </PhxBadge>
              </Box>
            }
            onDelete={clearParams}
            deleteIcon={<RemoveIcon />}
            size='medium'
            className='filter-chip'
          />
        ) : (
          // Default view: Individual chips
          activeFilters.map((filter) => (
            <Chip
              key={filter.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span className='filter-chip-label'>{filter.label}:</span>
                  <span>{filter.value}</span>
                </Box>
              }
              onDelete={filter.removeAction}
              deleteIcon={<RemoveIcon />}
              size='small'
              className='filter-chip'
            />
          ))
        )}
        {!consolidatedView && activeFilters.length > 0 && (
          <Typography
            variant='linkSm'
            component='a'
            onClick={clearParams}
            sx={{
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
              '&:active': {
                textDecoration: 'underline',
              },
            }}
          >
            {t('common.clearAll')}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default FiltersGroup;
